import { Box, Collapse, Typography } from '@mui/material';
import { HistoryEntry, EntryContent, CapsuleContent, CapsuleItem, CapsuleText } from './styles';
import { format } from 'date-fns';
import { abyssColors } from '@/theme/colors';
import { useMemo, useState } from 'react';
import { ExpandableCapsule } from './ExpandableCapsule';
import { localiseMeasurement } from '@/utils/unitSystem';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { PaintRegionStatus, UnitsEnum } from '@/__generated__/graphql';
import { usePaintRegionStatusLabels } from '@/hooks';
import { useAtomValue } from 'jotai';

type ExpandedCapsule = 'poi' | 'assembly' | null;

type POI = {
  id: string;
  name?: string;
  poiId?: string;
};

type Assembly = {
  id: string;
  name: string;
  area?: number;
};

export type HistoryEntryData = {
  createdAt: string;
  userName: string;
  pois: POI[];
  assemblies: Assembly[];
  area?: number | undefined;
  remediationAt?: string;
  lastModifiedFor?: string;
};

type HistoryEntryItemProps = {
  entry: HistoryEntryData;
  isFirst: boolean;
  isLast: boolean;
};

export const HistoryEntryItem = ({ entry, isFirst, isLast }: HistoryEntryItemProps) => {
  const [expandedCapsule, setExpandedCapsule] = useState<ExpandedCapsule>(null);
  const unitSystem = useAtomValue(unitSystemState);
  const { getPaintBlockLabels } = usePaintRegionStatusLabels();
  const hasArea = entry.area !== undefined && entry.area !== null;
  const timestamp = new Date(Number.parseInt(entry.createdAt, 10));
  const isValidTimestamp = !Number.isNaN(timestamp.getTime());
  const hasRemediationAt = entry.remediationAt !== undefined && entry.remediationAt !== null;
  const formattedTimestamp = isValidTimestamp
    ? format(timestamp, 'dd MMM yyyy hh:mm a')
    : 'Invalid date';

  // Detect if this is a completion status change or remediation edit based on lastModifiedFor pattern
  const isCompletionStatusChange = useMemo(() => {
    if (!entry.lastModifiedFor) return false;
    return (
      entry.lastModifiedFor.includes(`marked as ${PaintRegionStatus.Completed}`) ||
      entry.lastModifiedFor.includes('Proposed paint area') ||
      entry.lastModifiedFor.includes('Actual paint area') ||
      entry.lastModifiedFor.includes('remediation updated from') ||
      entry.lastModifiedFor.includes('proposed paint area updated from') ||
      entry.lastModifiedFor.includes('actual paint area updated from') ||
      entry.lastModifiedFor.includes('Ri0') ||
      entry.lastModifiedFor.includes('Chase area delta')
    );
  }, [entry.lastModifiedFor]);

  // Format remediation date if it exists
  const formattedRemediationDate = useMemo(() => {
    if (!hasRemediationAt || !entry.remediationAt) return '';
    // Handle both ISO 8601 format and Unix timestamp
    const remediationTimestamp = new Date(entry.remediationAt);
    if (Number.isNaN(remediationTimestamp.getTime())) return '';
    return format(remediationTimestamp, 'dd MMM yyyy');
  }, [hasRemediationAt, entry.remediationAt]);

  const poiCount = entry.pois.length;
  const assemblyCount = entry.assemblies.length;

  // Format area value with unit conversion
  const formattedArea = useMemo(() => {
    if (!hasArea) return '';
    return localiseMeasurement({
      unitSystem,
      sourceUnit: UnitsEnum.SquareMeter,
      value: entry.area,
      showUnitLabel: true,
      roundToDecimal: 2,
    });
  }, [hasArea, entry.area, unitSystem]);

  // Transform POIs into capsule items
  const poiItems = useMemo(
    () =>
      entry.pois.map((poi) => ({
        id: poi.id,
        displayName: poi.poiId
          ? `${poi.name || 'Unknown POI'} - ${poi.poiId}`
          : poi.name || 'Unknown POI',
      })),
    [entry.pois]
  );

  // Transform Assemblies into capsule items
  const assemblyItems = useMemo(
    () =>
      entry.assemblies.map((assembly, index) => ({
        id: `${assembly.name}_${index}`,
        displayName: assembly.name,
      })),
    [entry.assemblies]
  );

  // Generate label with proper pluralization
  const poiLabel = poiCount === 1 ? 'POI' : 'POIs';
  const assemblyLabel = assemblyCount === 1 ? 'Component' : 'Components';

  // Generate action description
  const actionDescription = useMemo(() => {
    const parts: string[] = [];

    if (poiCount > 0) {
      parts.push(`${poiCount} ${poiLabel}`);
    }

    if (assemblyCount > 0 && !hasArea) {
      parts.push(`${assemblyCount} ${assemblyLabel}`);
    }

    if (hasArea) {
      parts.push(formattedArea);
    }

    return parts.join(' and ');
  }, [poiCount, assemblyCount, hasArea, poiLabel, assemblyLabel, formattedArea]);

  // Extract message for completion status change and add unit labels to area values
  const completionMessage = useMemo(() => {
    if (!isCompletionStatusChange || !entry.lastModifiedFor) return '';
    let message = entry.lastModifiedFor;
    // Remove "by {userName}" if it exists in the message, we'll add it ourselves
    message = message.replace(/\s+by\s+.+$/, '');
    // Extract and format the area number with unit label
    // Pattern: "Proposed paint area {number}" or "Actual paint area {number}" or "Total area {number}"
    const proposedAreaMatch = message.match(/Proposed paint area (\d+(?:\.\d+)?)/);
    const actualAreaMatch = message.match(/Actual paint area (\d+(?:\.\d+)?)/);
    const totalAreaMatch = message.match(/Total area (\d+(?:\.\d+)?)/);
    // proposedAreaMatch is kept for backward compatibility — older history records stored in the
    // database may still contain "Proposed paint area" in their lastModifiedFor string.
    if (proposedAreaMatch) {
      const areaValue = parseFloat(proposedAreaMatch[1]);
      const formattedAreaWithUnit = localiseMeasurement({
        unitSystem,
        sourceUnit: UnitsEnum.SquareMeter,
        value: areaValue,
        showUnitLabel: true,
        roundToDecimal: 2,
      });
      message = message.replace(
        `Proposed paint area ${proposedAreaMatch[1]}`,
        `Proposed paint area ${formattedAreaWithUnit}`
      );
    } else if (actualAreaMatch) {
      const areaValue = parseFloat(actualAreaMatch[1]);
      const formattedAreaWithUnit = localiseMeasurement({
        unitSystem,
        sourceUnit: UnitsEnum.SquareMeter,
        value: areaValue,
        showUnitLabel: true,
        roundToDecimal: 2,
      });
      message = message.replace(
        `Actual paint area ${actualAreaMatch[1]}`,
        `Actual paint area ${formattedAreaWithUnit}`
      );
    } else if (totalAreaMatch) {
      const areaValue = parseFloat(totalAreaMatch[1]);
      const formattedAreaWithUnit = localiseMeasurement({
        unitSystem,
        sourceUnit: UnitsEnum.SquareMeter,
        value: areaValue,
        showUnitLabel: true,
        roundToDecimal: 2,
      });
      message = message.replace(
        `Total area ${totalAreaMatch[1]}`,
        `Total area ${formattedAreaWithUnit}`
      );
    }

    // Format bare numbers after PPA/FPPA/Actual Paint Area labels
    // e.g. "from PPA 4 to FPPA 2.636" or "to Actual Paint Area 7"
    message = message.replace(
      /(PPA|FPPA|Actual Paint Area)\s+(\d+(?:\.\d+)?)/g,
      (_match, label, numStr) => {
        const formatted = localiseMeasurement({
          unitSystem,
          sourceUnit: UnitsEnum.SquareMeter,
          value: parseFloat(numStr),
          showUnitLabel: true,
          roundToDecimal: 2,
        });
        return `${label} ${formatted}`;
      }
    );

    // Format numbers in "from X to Y" patterns (e.g. "updated from 20 to 3")
    message = message.replace(
      /\b(from)\s+(\d+(?:\.\d+)?)\s+(to)\s+(\d+(?:\.\d+)?)/g,
      (_match, from, oldNum, to, newNum) => {
        const formattedOld = localiseMeasurement({
          unitSystem,
          sourceUnit: UnitsEnum.SquareMeter,
          value: parseFloat(oldNum),
          showUnitLabel: true,
          roundToDecimal: 2,
        });
        const formattedNew = localiseMeasurement({
          unitSystem,
          sourceUnit: UnitsEnum.SquareMeter,
          value: parseFloat(newNum),
          showUnitLabel: true,
          roundToDecimal: 2,
        });
        return `${from} ${formattedOld} ${to} ${formattedNew}`;
      }
    );

    return message;
  }, [isCompletionStatusChange, entry.lastModifiedFor, unitSystem]);

  const textStyle = {
    fontSize: '12px',
    fontWeight: 400,
    color: abyssColors.primary[500],
  };

  return (
    <HistoryEntry isFirst={isFirst} isLast={isLast}>
      <EntryContent>
        {/* Timestamp */}
        <Typography
          variant="caption"
          sx={{
            fontSize: '12px',
            color: abyssColors.primary[400],
            marginTop: '4px',
            display: 'block',
          }}
        >
          {formattedTimestamp}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: 0.5 }}>
          {isCompletionStatusChange ? (
            // Completion status change message
            <>
              <Typography variant="body1" sx={textStyle}>
                {completionMessage} by{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 500,
                    color: abyssColors.primary[500],
                  }}
                >
                  {entry.userName}
                </Box>
              </Typography>
            </>
          ) : (
            // Regular remediation message
            <>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: abyssColors.primary[500],
                }}
              >
                {entry.userName}
              </Typography>
              <Typography variant="body1" sx={textStyle}>
                marked
              </Typography>
              <Typography variant="body1" sx={textStyle}>
                {actionDescription}
              </Typography>
              <Typography variant="body1" sx={textStyle}>
                as {getPaintBlockLabels(PaintRegionStatus.Completed).toLowerCase()}
              </Typography>
              {hasRemediationAt && formattedRemediationDate && (
                <Typography variant="body1" sx={textStyle}>
                  for date of {formattedRemediationDate}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Capsule Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          <ExpandableCapsule
            label={poiLabel}
            count={poiCount}
            isExpanded={expandedCapsule === 'poi'}
            onToggle={() => setExpandedCapsule(expandedCapsule === 'poi' ? null : 'poi')}
          />
          {!hasArea && (
            <ExpandableCapsule
              label={assemblyLabel}
              count={assemblyCount}
              isExpanded={expandedCapsule === 'assembly'}
              onToggle={() =>
                setExpandedCapsule(expandedCapsule === 'assembly' ? null : 'assembly')
              }
            />
          )}
          {/* Static area capsule with no toggle */}
          {hasArea && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                borderRadius: '16px',
                backgroundColor: abyssColors.primary[100],
                border: `1px solid ${abyssColors.primary[200]}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: abyssColors.primary[500],
                }}
              >
                {formattedArea}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Expandable Content - Shared Space Below Capsules */}
        <Collapse in={expandedCapsule !== null}>
          <CapsuleContent>
            {expandedCapsule === 'poi' &&
              poiItems.map((item) => (
                <CapsuleItem key={item.id}>
                  <CapsuleText variant="body2">{item.displayName}</CapsuleText>
                </CapsuleItem>
              ))}
            {expandedCapsule === 'assembly' &&
              assemblyItems.map((item) => (
                <CapsuleItem key={item.id}>
                  <CapsuleText variant="body2">{item.displayName}</CapsuleText>
                </CapsuleItem>
              ))}
          </CapsuleContent>
        </Collapse>
      </EntryContent>
    </HistoryEntry>
  );
};
